import { transformAdjustUrl } from './adjust';
import {
  transformAdriverEventPixelUrl,
  transformAdriverJs,
  transformAdriverUrl,
  transformAdriverVastUrl,
} from './adriver';
import { transformTnsUrl } from './tns';
import {
  transformWeboramaEventPixelUrl,
  transformWeboramaUrl,
  transformWeboramaVastUrl,
} from './weborama';
import { transformYandexUrl } from './yandex';
import {
  transformSizmekJs,
  transformSizmekUrl,
  transformSizmekVastUrl,
} from './sizmek';
import {
  transformTargetadsJs,
  transformTargetadsUrl,
  transformTargetadsVastUrl,
} from './targetads';
import { transformAdloxUrl } from './adloox';
import { transformOnelinkUrl } from './onelink';
import { jsCodes, pixelEventFields, pixelEventUrls } from '../index';
import { vastUrls } from '../vastUrlLinksFormat';

type TTransformer = (url: string, name: string) => string;

// Карта преобразователей для каждого типа ссылки
const urlTransformers: Record<string, TTransformer> = {
  // Adriver преобразователи
  'ad.adriver.ru/cgi-bin/rle.cgi': transformAdriverUrl,
  'ad.adriver.ru/cgi-bin/click.cgi': transformAdriverUrl,

  // TNS преобразователи
  'tns-counter.ru': transformTnsUrl,

  // Weborama преобразователи
  'wcm-ru.': transformWeboramaUrl,

  // Yandex преобразователи
  'amc.yandex.ru': transformYandexUrl,
  'amc.yandex.ru/show': transformYandexUrl,

  // Sizmek преобразователи
  'bs.serving-sys.ru': transformSizmekUrl,
  'bs.serving-sys.ru/Serving/adServer.bs?cn=display': transformSizmekUrl,

  // Targetads преобразователи
  'eye.targetads.io/view/click': transformTargetadsUrl,
  'eye.targetads.io/view/pixel': transformTargetadsUrl,

  // Adjust преобразователи
  'view.adjust.com/impression': transformAdjustUrl,

  // Adloox преобразователи
  'pixel.adlooxtracking.ru': transformAdloxUrl,

  // Onelink преобразователи
  'onelink.me': transformOnelinkUrl,
};

// Карта преобразователей для js code
const jsCodeTransformers: Record<string, TTransformer> = {
  // Adriver преобразователь
  'ad.adriver.ru/cgi-bin/json.cgi': transformAdriverJs,

  // Sizmek преобразователь
  'bs.serving-sys.ru': transformSizmekJs,

  // Targetads преобразователь
  'eye.targetads.io': transformTargetadsJs,
};

// Карта преобразователей для поля vast
const vastUrlTransformers: Record<string, TTransformer> = {
  // Adriver преобразователи
  'ad.adriver.ru/cgi-bin/rle.cgi': transformAdriverVastUrl,

  // Weborama преобразователи
  'wcm-ru.': transformWeboramaVastUrl,

  // Sizmek преобразователи
  'bs.serving-sys.ru': transformSizmekVastUrl,

  // Targetads преобразователи
  'eye.targetads.io': transformTargetadsVastUrl,
};

// Карта преобразователей для пикселей на событие
const eventPixelUrlTransformers: Record<string, TTransformer> = {
  // Adriver преобразователи
  'ev.adriver.ru/cgi-bin/event.cgi': transformAdriverEventPixelUrl,

  // Weborama преобразователи
  'wcm-ru.': transformWeboramaEventPixelUrl,
};

export const urls: string[] = [
  'ad.adriver.ru/cgi-bin/rle.cgi',
  'tns-counter.ru',
  'wcm-ru.',
  'amc.yandex.ru/show',
  'bs.serving-sys.ru/Serving/adServer.bs?cn=display',
  'eye.targetads.io/view/pixel',
  'view.adjust.com/impression',
  'pixel.adlooxtracking.ru',
  'ad.adriver.ru/cgi-bin/click.cgi',
  'amc.yandex.ru',
  'bs.serving-sys.ru',
  'eye.targetads.io/view/click',
  'onelink.me',
] as const;

/**
 *
 * @param url Значение поля
 * @param urlList Список доменов
 * @param name Название поля в котором находится ссылка
 * @returns Отформатированную ссылку
 */
export function formatUrl(
  url: string,
  urlList: string[],
  name: string,
): string {
  let formattedUrl = url.trim();

  // Определяем тип поля
  const isLink = name === 'link';
  const isPixel = [
    'click_audit_pixel',
    'pixel',
    'pixel_additional',
    'pixel_additional2',
  ].includes(name);

  let foundMatch = false;

  // Пробегаемся по массиву ссылок и ищем совпадение
  urlList.some((urlPattern) => {
    if (formattedUrl.includes(urlPattern)) {
      // Находим соответствующий преобразователь
      const transformer = findTransformerForPattern(
        urlPattern,
        urlTransformers,
      );

      if (transformer) {
        // Если нашли преобразователь - применяем его
        formattedUrl = transformer(formattedUrl, name);
        foundMatch = true;
        return true; // Прекращаем поиск после первого совпадения
      }
    }
    return false;
  });

  // Если не нашли совпадения в массиве и это поле link
  if (!foundMatch && isLink) {
    // Добавляем параметры для ссылок перехода
    const hasQuestionMark = formattedUrl.includes('?');
    const separator = hasQuestionMark ? '&' : '?';
    formattedUrl += `${separator}s_trk={TR_KEY}&erid={ERID}`;
  }

  if (!foundMatch && isPixel) {
    return formattedUrl;
  }

  // Для pixel полей без совпадения - просто возвращаем ссылку как есть

  return formattedUrl;
}

/**
 * Форматирование JS кода
 * @param jsCode Значение поля
 * @param name Название поля в котором находится JS код
 * @returns Отформатированный js code
 */
export function formatJsCode(jsCode: string, name: string): string {
  // Если не js_code_audit поле - возвращаем как есть
  if (name !== 'js_code_audit') {
    return jsCode;
  }

  let formattedJs = jsCode.trim();

  // Пробегаемся по массиву jsCodes и ищем совпадение
  jsCodes.some((urlPattern) => {
    if (formattedJs.includes(urlPattern)) {
      const transformer = jsCodeTransformers[urlPattern];

      if (transformer) {
        formattedJs = transformer(formattedJs, name);
        return true;
      }
    }
    return false;
  });

  return formattedJs;
}

/**
 * Форматирование JS кода
 * @param vast Vast cсылка
 * @param name Название поля в котором находится vast url код
 * @returns Отформатированный vast url
 */
export function formatVastUrl(vast: string, name: string): string {
  // Если не vast url поле - возвращаем как есть
  if (name !== 'data.vast_url') {
    return vast;
  }

  let formattedVastUrl = vast.trim();

  // Пробегаемся по массиву vast и ищем совпадение
  vastUrls.some((urlPattern) => {
    if (formattedVastUrl.includes(urlPattern)) {
      const transformer = vastUrlTransformers[urlPattern];

      if (transformer) {
        formattedVastUrl = transformer(formattedVastUrl, name);
        return true;
      }
    }
    return false;
  });

  return formattedVastUrl;
}

/**
 * Форматирование JS кода
 * @param pixel Event pixel cсылка
 * @param name Название поля в котором находится Event pixel код
 * @returns Отформатированный Event pixel
 */
export function formatEventPixelUrl(
  pixel: string,
  name: string,
): string {
  // Если не Event pixel поле - возвращаем как есть
  if (!pixelEventFields.includes(name)) {
    return pixel;
  }

  let formattedEventPixelUrl = pixel.trim();

  // Пробегаемся по массиву pixel и ищем совпадение
  pixelEventUrls.some((urlPattern) => {
    if (formattedEventPixelUrl.includes(urlPattern)) {
      const transformer = eventPixelUrlTransformers[urlPattern];

      if (transformer) {
        formattedEventPixelUrl = transformer(formattedEventPixelUrl, name);
        return true;
      }
    }
    return false;
  });
  return formattedEventPixelUrl;
}

function findTransformerForPattern<
  T extends typeof urlTransformers | typeof jsCodeTransformers,
>(pattern: string, transformer: T): TTransformer | null {
  // Ищем точное совпадение в карте преобразователей
  if (transformer[pattern]) {
    return transformer[pattern];
  }

  // Если нет точного совпадения, ищем частичное
  const result = Object.entries(transformer).find(
    ([key]) => pattern.includes(key) || key.includes(pattern),
  );

  return result ? result[1] : null;
}
